/**
 * The way into the record panel on mobile, shown above the composer.
 *
 * On mobile the chat is full screen and the panel opens over it on demand, so
 * without this the panel would be unreachable — and a draft arriving mid-
 * sentence would go unnoticed.
 *
 * Two jobs, deliberately in one place so there is exactly ONE control that
 * means "show me the record":
 *
 *  - **Something is waiting.** A pending draft the user has not seen. Stated,
 *    counted, and given a primary action, because the conversation cannot
 *    otherwise show it.
 *  - **Nothing is waiting, but a record exists.** The edit path — there is
 *    always something worth looking at, even with no unsaved change. Quieter:
 *    an offer, not a summons.
 *
 * On the create path before the first proposal there is genuinely nothing to
 * open, so it renders nothing rather than a button onto an empty panel.
 *
 * Desktop never renders this: the panel is already on screen.
 */

import { ArrowRight } from '@untitledui/icons';

import { Button } from '@/lib/base/buttons/button';

export interface BuilderReviewBarProps {
  /**
   * Pending proposals the user has not applied. More than one means they are
   * choosing between drafts, not just accepting one.
   */
  pendingCount: number;
  /**
   * Whether a saved record exists. True on the edit path from the start, and
   * on the create path once the first save lands.
   */
  hasRecord: boolean;
  /** What the panel contains, e.g. "automation". Used in the quiet variant. */
  recordLabel: string;
  onReview: () => void;
}

export function BuilderReviewBar({
  pendingCount,
  hasRecord,
  recordLabel,
  onReview,
}: BuilderReviewBarProps) {
  const hasPending = pendingCount > 0;

  // Nothing proposed and nothing saved — the create path before the agent's
  // first proposal. An "open the panel" control here would lead to the empty
  // state, which is worse than no control.
  if (!hasPending && !hasRecord) {
    return null;
  }

  if (!hasPending) {
    return (
      <div className='shrink-0 px-3 pb-2 sm:px-4'>
        <Button
          color='secondary'
          size='sm'
          onClick={onReview}
          className='w-full'
          iconTrailing={<ArrowRight className='size-4' />}
        >
          {`View ${recordLabel}`}
        </Button>
      </div>
    );
  }

  // "Ready to review" rather than "approve": approving is what the panel's
  // Save does, and the builder guidance is explicit that "approve" names a
  // control the user cannot reach from the conversation.
  const label =
    pendingCount === 1
      ? '1 change ready to review'
      : `${pendingCount} changes ready to review`;

  return (
    <div className='shrink-0 px-3 pb-2 sm:px-4'>
      <div className='flex items-center justify-between gap-3 rounded-lg border border-[var(--color-border-secondary)] bg-[var(--color-bg-secondary)] px-3 py-2'>
        <p className='min-w-0 truncate text-sm font-medium text-[var(--color-text-primary)]'>
          {label}
        </p>
        <Button
          size='sm'
          onClick={onReview}
          className='shrink-0'
          iconTrailing={<ArrowRight className='size-4' />}
        >
          Review
        </Button>
      </div>
    </div>
  );
}
